import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { HelpCircle, CalendarClock, Megaphone } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { api } from '../lib/api';
import { PageHeader, SectionHeader, Loading, EmptyState } from '../components/Shared';
import { ReviewBadge, SourceNote, SampleDataBanner } from '../components/DataBadge';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from 'recharts';

const PIE_COLORS = ['#1a2744', '#C9A84C', '#3d6a8a', '#2d7a55', '#b3542d', '#64748b'];

const ChartCard = ({ block, type, testId }) => {
  if (!block?.points?.length) return null;
  return (
    <Card data-testid={testId}>
      <CardContent className="p-5 sm:p-6">
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <h3 className="font-serif font-bold text-[#1a2744] text-lg leading-snug">{block.title}</h3>
          <ReviewBadge status={block.review_status} />
        </div>
        <div className="h-64 mt-4">
          <ResponsiveContainer width="100%" height="100%">
            {type === 'pie' ? (
              <PieChart>
                <Pie data={block.points} dataKey="value" nameKey="label" cx="50%" cy="50%" outerRadius={78}>
                  {block.points.map((p, i) => <Cell key={`${p.label || 'pt'}-${i}`} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            ) : (
              <BarChart data={block.points}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} interval={0} angle={-20} textAnchor="end" height={50} />
                <YAxis tick={{ fontSize: 12 }} width={40} />
                <Tooltip />
                <Bar dataKey="value" fill="#1a2744" radius={[4, 4, 0, 0]} />
              </BarChart>
            )}
          </ResponsiveContainer>
        </div>
        <SourceNote sourceName={block.source_name} sourceUrl={block.source_url} sourceNote={block.source_note} testId={`${testId}-source`} />
      </CardContent>
    </Card>
  );
};

export default function TaxTracker() {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/tax-tracker').then((r) => setContent(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <Loading />;
  if (!content) return <EmptyState title="Tax Tracker unavailable" subtitle="Please try again shortly." />;

  return (
    <div>
      <PageHeader kicker="TAXES & SPENDING" title="Ward 8 Tax Tracker" subtitle="How fast are property taxes rising, where do the dollars go, and what should residents be asking? Plain language, with sources." />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        <SampleDataBanner testId="taxtracker-sample-banner" />

        <p className="text-base text-slate-700 leading-relaxed mt-6 max-w-3xl" data-testid="taxtracker-intro">{content.intro}</p>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mt-8">
          <ChartCard block={content.levy_history} type="bar" testId="taxtracker-levy-chart" />
          <ChartCard block={content.where_dollars_go} type="pie" testId="taxtracker-dollar-chart" />
        </div>
        <div className="mt-5">
          <ChartCard block={content.operating_vs_capital} type="bar" testId="taxtracker-opcap-chart" />
        </div>

        {/* Budget timeline */}
        {content.timeline?.length > 0 && (
          <div className="mt-10">
            <SectionHeader kicker="HOW THE BUDGET WORKS" title="The budget year, step by step" dek="Knowing when decisions happen is how residents influence them before they are final." />
            <Card data-testid="taxtracker-timeline">
              <CardContent className="p-5 sm:p-6">
                {content.timeline.map((t, i) => (
                  <div key={`${t.period || ''}-${t.label || ''}-${i}`} className="flex gap-3">
                    <div className="flex flex-col items-center">
                      <span className={`h-3.5 w-3.5 rounded-full shrink-0 mt-1 ${i === 0 ? 'bg-[#C9A84C]' : 'bg-slate-300'}`} />
                      {i < content.timeline.length - 1 && <span className="w-0.5 flex-1 bg-slate-200" />}
                    </div>
                    <div className="pb-5">
                      <p className="text-xs font-bold uppercase tracking-wider text-[#C9A84C] flex items-center gap-1.5"><CalendarClock className="h-3.5 w-3.5" /> {t.period}</p>
                      <p className="font-semibold text-[#1a2744] mt-0.5">{t.label}</p>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Key questions */}
        {content.key_questions?.length > 0 && (
          <div className="mt-10">
            <SectionHeader kicker="ASK THE RIGHT QUESTIONS" title="Questions every taxpayer deserves answered" />
            <Card data-testid="taxtracker-questions">
              <CardContent className="p-2 sm:p-4">
                <Accordion type="single" collapsible>
                  {content.key_questions.map((q, i) => (
                    <AccordionItem key={`${String(q).slice(0, 40)}-${i}`} value={`q-${i}`}>
                      <AccordionTrigger className="text-left font-semibold text-[#1a2744] text-sm sm:text-base px-2" data-testid={`taxtracker-question-${i}`}>
                        <span className="flex items-start gap-2"><HelpCircle className="h-4 w-4 text-[#C9A84C] shrink-0 mt-0.5" /> {q}</span>
                      </AccordionTrigger>
                      <AccordionContent className="text-sm text-slate-600 px-2 leading-relaxed">
                        Bring this question to a budget meeting, a deputation, or an email to Council. The <Link to="/have-your-say" className="font-bold underline decoration-[#C9A84C] decoration-2 underline-offset-4">How to Have Your Say</Link> guide includes ready-to-use templates.
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </CardContent>
            </Card>
          </div>
        )}

        <Card className="mt-10 bg-[#1a2744] border-[#C9A84C]" data-testid="taxtracker-cta">
          <CardContent className="p-6 sm:p-8">
            <Megaphone className="h-8 w-8 text-[#C9A84C] mb-2" />
            <h3 className="font-serif font-black text-white text-xl">Think a number looks off?</h3>
            <p className="text-slate-300 mt-2 text-sm leading-relaxed">Tell the campaign. Verified corrections make this tracker stronger for every resident.</p>
            <div className="flex flex-col sm:flex-row gap-3 mt-5">
              <Button asChild className="h-12 bg-[#C9A84C] hover:bg-[#b8973f] text-[#1a2744] font-bold btn-press" data-testid="taxtracker-contact-cta"><Link to="/contact">Send a Correction</Link></Button>
              <Button asChild variant="outline" className="h-12 border-white/40 text-white hover:bg-white/10 font-bold btn-press bg-transparent" data-testid="taxtracker-havesay-cta"><Link to="/have-your-say">How to Have Your Say</Link></Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
